/**
 * Consolidated Analytics Utilities
 * Combines functionality from analyticsAccessibility.js, analyticsFormatters.js, and analyticsResponsive.js
 * Provides comprehensive analytics utilities with accessibility, formatting, and responsive design support
 */

import React from 'react';
import { breakpoints, getCurrentBreakpoint, isMobile, isTablet, isDesktop } from './responsiveUtils.js';

// ============================================================================
// FORMATTING UTILITIES
// ============================================================================

/**
 * Format duration in seconds to human readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration (e.g., "3:45", "1h 23m")
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
};

/**
 * Format cost in dollars to currency string
 * @param {number} cost - Cost in dollars
 * @returns {string} Formatted cost (e.g., "$12.34")
 */
export const formatCost = (cost) => {
  if (!cost || cost === 0) return '$0.00';
  return `$${cost.toFixed(2)}`;
};

/**
 * Format percentage with one decimal place
 * @param {number} percentage - Percentage value
 * @returns {string} Formatted percentage (e.g., "94.5%")
 */
export const formatPercentage = (percentage) => {
  if (!percentage && percentage !== 0) return '0.0%';
  return `${percentage.toFixed(1)}%`;
};

/**
 * Format large numbers with commas
 * @param {number} number - Number to format
 * @returns {string} Formatted number (e.g., "1,234")
 */
export const formatNumber = (number) => {
  if (!number && number !== 0) return '0';
  return number.toLocaleString();
};

/**
 * Format bytes to human readable format
 * @param {number} bytes - Number of bytes
 * @returns {string} Formatted size (e.g., "1.2 MB")
 */
export const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

/**
 * Format date to relative time (e.g., "2 hours ago")
 * @param {Date|string} date - Date to format
 * @returns {string} Relative time string
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const targetDate = new Date(date);
  const diffInSeconds = Math.floor((now - targetDate) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  
  return targetDate.toLocaleDateString();
};

// ============================================================================
// ACCESSIBILITY UTILITIES
// ============================================================================

/**
 * Generate accessible labels for chart data
 */
export const generateChartAccessibilityLabels = (data, chartType, options = {}) => {
  const { xKey = 'x', yKey = 'y', labelKey = 'label', valueKey = 'value' } = options;
  
  switch (chartType) {
    case 'bar':
      return data.map((item, index) => ({
        ...item,
        ariaLabel: `${item[xKey]}: ${item[yKey]}`,
        ariaDescription: `Bar ${index + 1} of ${data.length}. Value: ${item[yKey]}`,
        tabIndex: 0,
        role: 'img'
      }));
    
    case 'line':
      return data.map((item, index) => ({
        ...item,
        ariaLabel: `${item[xKey]}: ${item[yKey]}`,
        ariaDescription: `Data point ${index + 1} of ${data.length}. ${item[xKey]} has value ${item[yKey]}`,
        tabIndex: 0,
        role: 'img'
      }));
    
    case 'donut':
    case 'pie':
      return data.map((item, index) => ({
        ...item,
        ariaLabel: `${item[labelKey || xKey]}: ${item[valueKey || yKey]}`,
        ariaDescription: `Segment ${index + 1} of ${data.length}. ${item[labelKey || xKey]} represents ${item[valueKey || yKey]}`,
        tabIndex: 0,
        role: 'img'
      }));
    
    default:
      return data.map((item, index) => ({
        ...item,
        ariaLabel: `Data point ${index + 1}: ${item[yKey]}`,
        ariaDescription: `Data point ${index + 1} of ${data.length}`,
        tabIndex: 0,
        role: 'img'
      }));
  }
};

/**
 * Generate accessible table headers for analytics data
 */
export const generateAccessibleTableHeaders = (columns) => {
  return columns.map(column => ({
    ...column,
    role: 'columnheader',
    scope: 'col',
    ariaSort: column.sortable ? 'none' : undefined,
    tabIndex: column.sortable ? 0 : -1
  }));
};

/**
 * Generate accessible summary for analytics data
 */
export const generateAnalyticsSummary = (data, type = 'general') => {
  const totalItems = data.length;
  
  switch (type) {
    case 'calls':
      const totalCalls = data.reduce((sum, item) => sum + (item.calls || 0), 0);
      const avgDuration = data.reduce((sum, item) => sum + (item.duration || 0), 0) / totalItems;
      return `Analytics showing ${totalItems} entries with ${totalCalls} total calls and average duration of ${formatDuration(avgDuration)}`;
    
    case 'revenue':
      const totalRevenue = data.reduce((sum, item) => sum + (item.revenue || 0), 0);
      return `Revenue analytics showing ${totalItems} entries with total revenue of ${formatCost(totalRevenue)}`;
    
    case 'usage':
      const totalUsage = data.reduce((sum, item) => sum + (item.usage || 0), 0);
      return `Usage analytics showing ${totalItems} entries with total usage of ${formatNumber(totalUsage)}`;
    
    default:
      return `Analytics table with ${totalItems} entries`;
  }
};

// ============================================================================
// RESPONSIVE DESIGN UTILITIES
// ============================================================================

/**
 * Get responsive classes for analytics dashboard layout
 */
export const getAnalyticsDashboardClasses = () => {
  return {
    container: 'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    header: 'mb-6 sm:mb-8',
    title: 'text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2',
    subtitle: 'text-sm sm:text-base text-gray-600 dark:text-gray-400',
    section: 'mb-6 sm:mb-8 lg:mb-10',
    sectionTitle: 'text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6'
  };
};

/**
 * Get responsive classes for metrics grid
 */
export const getMetricsGridClasses = (metricCount = 4) => {
  const baseClasses = 'grid gap-4 sm:gap-5 lg:gap-6';
  
  if (metricCount <= 2) {
    return `${baseClasses} grid-cols-1 sm:grid-cols-2`;
  } else if (metricCount <= 4) {
    return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-4`;
  } else {
    return `${baseClasses} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`;
  }
};

/**
 * Get responsive classes for metric cards
 */
export const getMetricCardClasses = () => {
  return {
    container: 'overflow-hidden rounded-lg px-4 py-5 sm:px-6 sm:py-6 shadow border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700',
    title: 'truncate text-sm font-medium text-gray-500 dark:text-gray-400',
    value: 'mt-1 text-2xl sm:text-3xl font-semibold tracking-tight text-gray-900 dark:text-white',
    description: 'mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400'
  };
};

/**
 * Get responsive classes for charts
 */
export const getChartClasses = (chartType = 'default') => {
  const baseClasses = 'w-full';
  
  switch (chartType) {
    case 'bar':
      return `${baseClasses} h-64 sm:h-80 lg:h-96`;
    case 'line':
      return `${baseClasses} h-48 sm:h-64 lg:h-80`;
    case 'pie':
    case 'donut':
      return `${baseClasses} h-64 sm:h-80`;
    default:
      return `${baseClasses} h-64 sm:h-80 lg:h-96`;
  }
};

/**
 * Get responsive table classes
 */
export const getAnalyticsTableClasses = () => {
  return {
    container: 'overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg',
    wrapper: 'overflow-x-auto',
    table: 'min-w-full divide-y divide-gray-300 dark:divide-gray-700',
    header: 'bg-gray-50 dark:bg-gray-800',
    headerCell: 'px-3 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide sm:px-6',
    body: 'bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800',
    row: 'hover:bg-gray-50 dark:hover:bg-gray-800',
    cell: 'whitespace-nowrap px-3 py-4 text-sm text-gray-900 dark:text-gray-100 sm:px-6'
  };
};

/**
 * Get responsive chart configuration based on screen size
 */
export const getResponsiveChartConfig = (baseConfig = {}) => {
  const currentBreakpoint = getCurrentBreakpoint();
  
  const responsiveConfig = {
    ...baseConfig,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      ...baseConfig.plugins,
      legend: {
        ...baseConfig.plugins?.legend,
        display: !isMobile(),
        position: isMobile() ? 'bottom' : 'top'
      },
      tooltip: {
        ...baseConfig.plugins?.tooltip,
        titleFont: {
          size: isMobile() ? 12 : 14
        },
        bodyFont: {
          size: isMobile() ? 11 : 13
        }
      }
    },
    scales: {
      ...baseConfig.scales,
      x: {
        ...baseConfig.scales?.x,
        ticks: {
          ...baseConfig.scales?.x?.ticks,
          font: {
            size: isMobile() ? 10 : 12
          },
          maxRotation: isMobile() ? 45 : 0
        }
      },
      y: {
        ...baseConfig.scales?.y,
        ticks: {
          ...baseConfig.scales?.y?.ticks,
          font: {
            size: isMobile() ? 10 : 12
          }
        }
      }
    }
  };
  
  return responsiveConfig;
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate analytics metrics from raw data
 */
export const calculateMetrics = (data, type = 'calls') => {
  if (!data || data.length === 0) {
    return {
      total: 0,
      average: 0,
      min: 0,
      max: 0,
      change: 0
    };
  }
  
  const values = data.map(item => item[type] || 0);
  const total = values.reduce((sum, val) => sum + val, 0);
  const average = total / values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  // Calculate change from previous period (assuming data is sorted chronologically)
  const currentPeriod = values.slice(-Math.ceil(values.length / 2));
  const previousPeriod = values.slice(0, Math.floor(values.length / 2));
  
  const currentAvg = currentPeriod.reduce((sum, val) => sum + val, 0) / currentPeriod.length;
  const previousAvg = previousPeriod.reduce((sum, val) => sum + val, 0) / previousPeriod.length;
  const change = previousAvg > 0 ? ((currentAvg - previousAvg) / previousAvg) * 100 : 0;
  
  return {
    total,
    average,
    min,
    max,
    change
  };
};

/**
 * Generate color palette for charts
 */
export const generateChartColors = (count, type = 'default') => {
  const palettes = {
    default: [
      '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444',
      '#06B6D4', '#84CC16', '#F97316', '#EC4899', '#6366F1'
    ],
    success: [
      '#10B981', '#059669', '#047857', '#065F46', '#064E3B'
    ],
    warning: [
      '#F59E0B', '#D97706', '#B45309', '#92400E', '#78350F'
    ],
    error: [
      '#EF4444', '#DC2626', '#B91C1C', '#991B1B', '#7F1D1D'
    ]
  };
  
  const palette = palettes[type] || palettes.default;
  const colors = [];
  
  for (let i = 0; i < count; i++) {
    colors.push(palette[i % palette.length]);
  }
  
  return colors;
};

/**
 * Export analytics data to CSV
 */
export const exportToCSV = (data, filename = 'analytics-export.csv') => {
  if (!data || data.length === 0) return;
  
  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes in CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(',')
    )
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Format usage quota display
 * @param {number} used - Used amount
 * @param {number} total - Total quota
 * @returns {string} Formatted quota (e.g., "150 / 1000")
 */
export const formatUsageQuota = (used, total) => {
  if (!used && used !== 0) used = 0;
  if (!total && total !== 0) total = 0;
  return `${formatNumber(used)} / ${formatNumber(total)}`;
};

/**
 * Format date for display
 * @param {string|Date} date - Date to format
 * @returns {string} Formatted date
 */
export const formatDate = (date) => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return 'Invalid Date';
  return dateObj.toLocaleDateString();
};

/**
 * Get time range label for display
 * @param {string} timeRange - Time range key
 * @returns {string} Human readable label
 */
export const getTimeRangeLabel = (timeRange) => {
  const labels = {
    '24h': 'Last 24 Hours',
    '7d': 'Last 7 Days',
    '30d': 'Last 30 Days',
    '90d': 'Last 90 Days',
    'all': 'All Time'
  };
  return labels[timeRange] || 'Unknown Range';
};

// Export all utilities
export default {
  // Formatting
  formatDuration,
  formatCost,
  formatPercentage,
  formatNumber,
  formatBytes,
  formatRelativeTime,
  formatUsageQuota,
  formatDate,
  getTimeRangeLabel,
  
  // Accessibility
  generateChartAccessibilityLabels,
  generateAccessibleTableHeaders,
  generateAnalyticsSummary,
  
  // Responsive Design
  getAnalyticsDashboardClasses,
  getMetricsGridClasses,
  getMetricCardClasses,
  getChartClasses,
  getAnalyticsTableClasses,
  getResponsiveChartConfig,
  
  // Utilities
  calculateMetrics,
  generateChartColors,
  exportToCSV
};
